import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Play } from 'lucide-react';

interface RuleSummary {
  code: string;
  descripcion: string;
  severity: 'ok' | 'observacion' | 'bloqueante';
  listas_evaluadas: number;
  listas_pasa: number;
  listas_falla: number;
  listas_falla_unicas: number;
  candidatos_afectados: number;
  extra: Record<string, any>;
}

interface RunOutput {
  rules: RuleSummary[];
  states: {
    candidatos_bloqueante: number;
    candidatos_observacion: number;
    candidatos_ok: number;
    listas_bloqueante: number;
    listas_observacion: number;
  };
}

const SEV_PILL: Record<string, string> = {
  ok: 'bg-green-100 text-green-700',
  observacion: 'bg-amber-100 text-amber-700',
  bloqueante: 'bg-red-100 text-red-700',
};

export function Validacion() {
  const qc = useQueryClient();
  const [last, setLast] = useState<RunOutput | null>(null);

  const { data: kpis } = useQuery<{ paridad_violada_formulas: number; alternancia_violada_listas: number }>({
    queryKey: ['kpis'],
    queryFn: async () => (await api.get('/dashboard/kpis')).data,
  });

  const mut = useMutation({
    mutationFn: async () => (await api.post<RunOutput>('/validacion/run')).data,
    onSuccess: (data) => {
      setLast(data);
      qc.invalidateQueries({ queryKey: ['kpis'] });
      qc.invalidateQueries({ queryKey: ['listas'] });
      qc.invalidateQueries({ queryKey: ['cands'] });
    },
  });

  return (
    <div className="p-6 space-y-5 max-w-6xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-arpp-primary">Motor de Validación</h1>
          <p className="text-xs text-gray-600">
            Corre las reglas V01-V18 sobre las 1,206 listas y persiste estados
          </p>
        </div>
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="flex items-center gap-2 bg-arpp-primary text-white px-4 py-2 rounded text-sm hover:bg-blue-800 disabled:opacity-50"
        >
          <Play size={16} /> {mut.isPending ? 'Corriendo…' : 'Correr validación ahora'}
        </button>
      </header>

      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-xs text-blue-900">
        <strong>Severidades:</strong>{' '}
        <span className="text-red-700">🔴 BLOQUEANTE</span> no puede presentarse al JEE ·{' '}
        <span className="text-amber-700">⚠ OBSERVACIÓN</span> subsanable antes del 16-jun ·{' '}
        <span className="text-green-700">🟢 OK</span> cumple regla
      </div>

      {last && (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="border rounded p-3 bg-red-50 border-red-200">
            <div className="text-2xl font-bold">{last.states.candidatos_bloqueante.toLocaleString('es-PE')}</div>
            <div className="text-xs text-gray-700">Candidatos BLOQUEANTE</div>
          </div>
          <div className="border rounded p-3 bg-amber-50 border-amber-200">
            <div className="text-2xl font-bold">{last.states.candidatos_observacion.toLocaleString('es-PE')}</div>
            <div className="text-xs text-gray-700">Candidatos OBSERVACIÓN</div>
          </div>
          <div className="border rounded p-3 bg-green-50 border-green-200">
            <div className="text-2xl font-bold">{last.states.candidatos_ok.toLocaleString('es-PE')}</div>
            <div className="text-xs text-gray-700">Candidatos OK</div>
          </div>
          <div className="border rounded p-3 bg-red-50 border-red-200">
            <div className="text-2xl font-bold">{last.states.listas_bloqueante.toLocaleString('es-PE')}</div>
            <div className="text-xs text-gray-700">Listas BLOQUEANTE</div>
          </div>
          <div className="border rounded p-3 bg-amber-50 border-amber-200">
            <div className="text-2xl font-bold">{last.states.listas_observacion.toLocaleString('es-PE')}</div>
            <div className="text-xs text-gray-700">Listas OBSERVACIÓN</div>
          </div>
        </section>
      )}

      <div className="bg-white border rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <th className="px-3 py-2 text-left">Cód</th>
              <th className="px-3 py-2 text-left">Regla</th>
              <th className="px-3 py-2 text-left">Sev.</th>
              <th className="px-3 py-2 text-right">Evaluadas</th>
              <th className="px-3 py-2 text-right">Pasa</th>
              <th className="px-3 py-2 text-right">Falla</th>
              <th className="px-3 py-2 text-right">Listas afectadas (únicas)</th>
              <th className="px-3 py-2 text-right">Candidatos afectados</th>
            </tr>
          </thead>
          <tbody>
            {!last && (
              <tr><td colSpan={8} className="p-6 text-center text-gray-500 text-xs">
                Aún no se ha corrido el motor. Haz click en "Correr validación ahora".
                {kpis && <div className="mt-2 text-amber-700">
                  Cifras KPI actuales pueden estar desactualizadas (se refrescan tras correr).
                </div>}
              </td></tr>
            )}
            {last?.rules.map((r) => (
              <tr key={r.code} className={`border-t ${r.listas_falla > 0 ? 'bg-amber-50/30' : ''}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                <td className="px-3 py-2 text-xs">{r.descripcion}</td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] px-2 py-0.5 rounded ${SEV_PILL[r.severity]}`}>
                    {r.severity}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">{r.listas_evaluadas.toLocaleString('es-PE')}</td>
                <td className="px-3 py-2 text-right text-green-700">{r.listas_pasa.toLocaleString('es-PE')}</td>
                <td className="px-3 py-2 text-right text-red-700">{r.listas_falla.toLocaleString('es-PE')}</td>
                <td className="px-3 py-2 text-right">{r.listas_falla_unicas.toLocaleString('es-PE')}</td>
                <td className="px-3 py-2 text-right">{r.candidatos_afectados.toLocaleString('es-PE')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {last?.rules.find((r) => r.code === 'V06')?.extra?.designados_debe_ser_joven ? (
        <div className="text-xs text-gray-600">
          <strong>V06 extra:</strong> {last.rules.find((r) => r.code === 'V06')?.extra.designados_debe_ser_joven}{' '}
          posiciones designadas deben asignarse a candidatos JÓVENES (≤29 años al 16-jun-2026)
          para cumplir la cuota del 20%.
        </div>
      ) : null}
    </div>
  );
}
